/* ==========================================================================
   Crumpaw · Portrait Bowl — hero behaviour
   Loaded only by sections/cp-prt-product-hero.liquid.

   Part 1 is cp-sb21-hero.js cloned byte for byte: the dock offset, the
   preview mirror and the band centring behave exactly as they do on 2.1.
   The mirror picks up the photo cut-out for free, because it syncs whatever
   is inside the preview frame.

   Part 2, at the bottom, is Step 2: the upload, the crop, the cut-out in the
   live preview, and two pieces of copy the shared script cannot own.

   Nothing here is loaded by any other template, and nothing here mutates
   cp-signature-bowl.js or the 2.1 hero.
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE = '(max-width: 749px)';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function isHook(name) {
    return name.indexOf('data-cpsb') === 0 || name === 'id' || name === 'for';
  }

  /* ---- 1. how much is pinned above us ---------------------------------- */

  function pinnedHeight(root) {
    var selector = root.getAttribute('data-cpsb21-sticky-selector');
    if (!selector) return 0;

    var total = 0;
    var seen = [];
    selector.split(',').forEach(function (part) {
      var sel = part.trim();
      if (!sel) return;
      var nodes;
      try {
        nodes = document.querySelectorAll(sel);
      } catch (error) {
        return; /* a mistyped selector must never break the page */
      }
      Array.prototype.forEach.call(nodes, function (node) {
        if (seen.indexOf(node) !== -1) return;
        var position = window.getComputedStyle(node).position;
        if (position !== 'sticky' && position !== 'fixed') return;
        var rect = node.getBoundingClientRect();
        if (rect.height <= 0) return;
        seen.push(node);
        total += rect.height;
      });
    });
    return total;
  }

  function measure(root) {
    root.style.setProperty('--cpsb21-dock-top', pinnedHeight(root).toFixed(1) + 'px');
  }

  /* ---- 2. keep the copy in step with the real preview -------------------- */

  function scrub(node) {
    if (node.nodeType !== 1) return;
    Array.prototype.slice.call(node.attributes).forEach(function (attr) {
      if (isHook(attr.name)) node.removeAttribute(attr.name);
    });
    node.removeAttribute('fetchpriority');
    Array.prototype.forEach.call(node.children, scrub);
  }

  function copyAttributes(from, to, isRoot) {
    var wanted = {};
    Array.prototype.forEach.call(from.attributes, function (attr) {
      if (isHook(attr.name)) return;
      if (isRoot && attr.name === 'class') return; /* the copy carries its own */
      wanted[attr.name] = attr.value;
    });
    Array.prototype.slice.call(to.attributes).forEach(function (attr) {
      if (attr.name in wanted) return;
      if (attr.name === 'aria-hidden') return;
      if (isRoot && (attr.name === 'class' || attr.name === 'style')) return;
      to.removeAttribute(attr.name);
    });
    Object.keys(wanted).forEach(function (name) {
      if (to.getAttribute(name) !== wanted[name]) to.setAttribute(name, wanted[name]);
    });
  }

  /* Returns false when the shapes have diverged and a rebuild is needed. */
  function sync(from, to, isRoot) {
    if (from.tagName !== to.tagName) return false;
    if (from.children.length !== to.children.length) return false;
    copyAttributes(from, to, isRoot);
    if (!from.children.length) {
      if (to.textContent !== from.textContent) to.textContent = from.textContent;
      return true;
    }
    for (var i = 0; i < from.children.length; i++) {
      if (!sync(from.children[i], to.children[i], false)) return false;
    }
    return true;
  }

  function build(frame, into, className) {
    var copy = frame.cloneNode(true);
    scrub(copy);
    copy.classList.add(className);
    copy.setAttribute('aria-hidden', 'true');
    into.replaceChildren(copy);
    return copy;
  }

  /* The band's position depends on the preview padding and baseline
     settings, so measure where the name actually sits and centre on it
     rather than trusting a percentage. */
  function centreBand(crop, copy) {
    var cropHeight = crop.clientHeight;
    if (!cropHeight) return;
    var row = copy.querySelector('.cpsb-preview__row--main');
    if (!row) return;

    // The lockup is the cut-out, the name and the printed rule together. The
    // row is a zero height line at the *bottom* of it, because the cut-out and
    // the name are bottom aligned onto the baseline — so centring the crop on
    // the row alone pushes the cut-out out through the top. Measure the three
    // parts and centre what they actually span.
    //
    // Offsets are taken against the copy's own box, so this is unaffected by
    // whatever transform is already on it from the previous pass.
    var parts = [
      copy.querySelector('.cpsb-preview__art--left'),
      copy.querySelector('.cpsb-preview__name'),
      copy.querySelector('.cpprt-baseline')
    ];
    var base = copy.getBoundingClientRect();
    var top = Infinity;
    var bottom = -Infinity;

    parts.forEach(function (node) {
      if (!node) return;
      var box = node.getBoundingClientRect();
      if (!box.height) return;
      top = Math.min(top, box.top - base.top);
      bottom = Math.max(bottom, box.bottom - base.top);
    });

    if (top === Infinity || bottom <= top) {
      // Nothing measurable yet — fall back to the old behaviour rather than
      // leaving the crop unpositioned.
      var offset = row.offsetTop;
      if (!offset) return;
      copy.style.transform = 'translateY(' + (cropHeight / 2 - offset).toFixed(1) + 'px)';
      return;
    }

    var centre = (top + bottom) / 2;
    copy.style.transform = 'translateY(' + (cropHeight / 2 - centre).toFixed(1) + 'px)';
  }

  function Mirror(root) {
    this.root = root;
    this.dock = root.querySelector('[data-cpsb21="dock"]');
    this.crop = root.querySelector('[data-cpsb21="dock-crop"]');
    this.dialog = root.querySelector('[data-cpsb21="full"]');
    this.fullBody = root.querySelector('[data-cpsb21="full-body"]');
    this.fullConfig = root.querySelector('[data-cpsb21="full-config"]');
    this.copy = null;
    this.fullCopy = null;
    this.frame = null;
    this.observer = null;
    this.queued = false;
  }

  Mirror.prototype.frameNode = function () {
    /* Re-resolved every pass: the theme may replace the node outright, and a
       stale reference would silently freeze the copy. */
    var frame = this.root.querySelector('[data-cpsb="preview-frame"]');
    if (frame !== this.frame) {
      this.frame = frame;
      this.copy = null;
      this.watch();
    }
    return frame;
  };

  Mirror.prototype.watch = function () {
    if (this.observer) this.observer.disconnect();
    if (!this.frame || !window.MutationObserver) return;
    var self = this;
    this.observer = new MutationObserver(function () { self.schedule(); });
    this.observer.observe(this.frame, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true
    });
  };

  Mirror.prototype.schedule = function () {
    if (this.queued) return;
    this.queued = true;
    var self = this;
    window.requestAnimationFrame(function () {
      self.queued = false;
      self.paint();
    });
  };

  Mirror.prototype.paint = function () {
    var frame = this.frameNode();
    if (!frame) return;

    if (this.crop) {
      if (!this.copy || !this.copy.isConnected || !sync(frame, this.copy, true)) {
        this.copy = build(frame, this.crop, 'cpsb21-dock__inner');
      }
      centreBand(this.crop, this.copy);
      if (this.dock) this.dock.setAttribute('data-cpsb21-ready', 'on');
    }

    /* The dialog copy is only worth keeping current while it is on screen. */
    if (this.fullBody && this.isOpen()) {
      if (!this.fullCopy || !this.fullCopy.isConnected || !sync(frame, this.fullCopy, true)) {
        this.fullCopy = build(frame, this.fullBody, 'cpsb21-full__inner');
      }
      if (this.fullConfig) {
        var config = frame.querySelector('.cpsb-preview__config');
        this.fullConfig.textContent = config ? config.textContent.trim() : '';
      }
    }
  };

  Mirror.prototype.isOpen = function () {
    return Boolean(this.dialog && this.dialog.open);
  };

  Mirror.prototype.open = function (trigger) {
    if (!this.dialog || this.isOpen()) return;
    this.opener = trigger || null;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', ''); /* very old Safari */
    }
    this.paint();
  };

  Mirror.prototype.close = function () {
    if (!this.dialog || !this.isOpen()) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  };

  Mirror.prototype.start = function () {
    if (!this.dock || !this.crop) return false;
    this.paint();
    var self = this;
    /* Watch the section for the frame being replaced wholesale rather than
       mutated. The observer above is bound to a node that would be detached
       by that, and the copy would silently freeze on its last state.
       Mutations inside the dock are our own writes and are ignored, so this
       cannot feed itself. */
    if (window.MutationObserver) {
      this.structure = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          if (!self.dock.contains(records[i].target)) {
            self.schedule();
            return;
          }
        }
      });
      this.structure.observe(this.root, { childList: true, subtree: true });
    }
    /* Second trigger: the section's own controls. Runs after the theme's own
       handler on the same event, so the copy picks up whatever it rendered. */
    ['change', 'input'].forEach(function (type) {
      self.root.addEventListener(type, function () { self.schedule(); }, true);
    });
    /* Third: late-arriving images change the geometry the band was centred on. */
    window.addEventListener('load', function () { self.schedule(); }, { once: true });

    /* Both the Enlarge link and the full-preview button open the same dialog;
       the ✕ and Keep editing buttons close it. Esc is handled by <dialog>. */
    this.root.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-cpsb21="open-full"]');
      if (opener) {
        event.preventDefault();
        self.open(opener);
        return;
      }
      if (event.target.closest('[data-cpsb21="close-full"]')) {
        event.preventDefault();
        self.close();
      }
    });

    if (this.dialog) {
      /* Native dialogs do not restore focus themselves. */
      this.dialog.addEventListener('close', function () {
        if (self.opener && self.opener.isConnected) self.opener.focus();
        self.opener = null;
      });
      /* Clicking the backdrop lands on the dialog itself, not the card. */
      this.dialog.addEventListener('click', function (event) {
        if (event.target === self.dialog) self.close();
      });
    }
    return true;
  };

  Mirror.prototype.stop = function () {
    if (this.observer) this.observer.disconnect();
    if (this.structure) this.structure.disconnect();
    this.observer = null;
    this.structure = null;
  };

  /* ---- 3. a chosen print colour survives a bowl colour change ------------
     cp-signature-bowl.js clears the ink override whenever the colourway
     changes — "Matches the design: changing colourway resets the ink
     override". That file is shared with four templates, so rather than edit
     it, the choice is put back afterwards: remember the checked ink before
     the reset, re-select it after, and let the shared handler re-render
     through its own code path.

     Only a deliberate choice is restored. A customer who has never touched
     the print colour still gets each colourway's default, which is the point
     of the original behaviour — a pale band starts on dark ink. */

  function keepInk(root) {
    if (root.getAttribute('data-cpsb21-keep-ink') !== 'on') return;

    var chosen = false;
    var remembered = null;

    var checkedInk = function () {
      return root.querySelector('[data-cpsb="ink-input"]:checked');
    };

    var isSwatch = function (target) {
      return target && target.closest && target.closest('[data-cpsb="swatch"]');
    };

    var isColour = function (target) {
      return target && target.matches && target.matches('[data-cpsb="colour-input"]');
    };

    /* Capture runs before the control's own handler, so this still sees the
       ink that was selected before the reset. */
    root.addEventListener('change', function (event) {
      if (event.target && event.target.matches
        && event.target.matches('[data-cpsb="ink-input"]')) {
        chosen = true;
        return;
      }
      if (isColour(event.target)) remembered = chosen ? checkedInk() : null;
    }, true);

    /* The swatch strip switches colour on click without firing a change. */
    root.addEventListener('click', function (event) {
      if (chosen && isSwatch(event.target)) remembered = checkedInk();
    }, true);

    var restore = function () {
      var ink = remembered;
      remembered = null;
      if (!ink || !ink.isConnected || ink.checked) return;
      ink.checked = true;
      ink.dispatchEvent(new Event('change', { bubbles: true }));
    };

    /* Bubbling reaches the section after the control's own handler has run
       and re-rendered, so by here the reset has already happened. */
    root.addEventListener('change', function (event) {
      if (isColour(event.target)) restore();
    });
    root.addEventListener('click', function (event) {
      if (isSwatch(event.target)) restore();
    });
  }

  /* ---- 4. bring the chosen chip into view, once ------------------------- */

  function centreSelected(row) {
    var checked = row.querySelector('input:checked');
    if (!checked || !checked.id) return;
    var label;
    try {
      label = row.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(checked.id) : checked.id) + '"]');
    } catch (error) {
      return;
    }
    if (!label) return;
    /* Set scrollLeft directly. scrollIntoView would scroll the page as well,
       which would move Step 2 out from under the customer. */
    var target = label.offsetLeft - (row.clientWidth - label.offsetWidth) / 2;
    row.scrollLeft = Math.max(0, target);
  }

  function armRows(root) {
    var rows = root.querySelectorAll(
      '[data-cpsb21-row] .cpsb-inks, [data-cpsb21-row] .cpsb-fonts,' +
      ' [data-cpsb21-row] .cpsb-designs, [data-cpsb21-colour-row] .cpsb-chips');
    Array.prototype.forEach.call(rows, function (row) {
      if (row.dataset.cpsb21Touched === 'on') return;
      ['pointerdown', 'wheel', 'touchstart'].forEach(function (event) {
        row.addEventListener(event, function () {
          row.dataset.cpsb21Touched = 'on';
        }, { passive: true, once: true });
      });
      window.requestAnimationFrame(function () {
        if (row.dataset.cpsb21Touched !== 'on') centreSelected(row);
      });
    });
  }

  /* ---- wiring ----------------------------------------------------------- */

  function init(root) {
    if (root.dataset.cpsb21Init === 'on') return;
    root.dataset.cpsb21Init = 'on';

    var media = window.matchMedia(MOBILE);
    var mirror = null;

    /* Not viewport-dependent: the print colour should hold at any width. */
    keepInk(root);

    /* The docked strip, the Enlarge link and the full-preview button are
       mobile-only. They ship hidden so the desktop cascade never sees them,
       and are unhidden here rather than by a CSS rule outside the query. */
    var mobileOnly = root.querySelectorAll('[data-cpsb21-mobile-only]');
    var reveal = function (on) {
      Array.prototype.forEach.call(mobileOnly, function (el) { el.hidden = !on; });
    };

    var apply = function () {
      if (!media.matches) {
        reveal(false);
        if (mirror) {
          mirror.close();
          mirror.stop();
          mirror = null;
        }
        return;
      }
      reveal(true);
      measure(root);
      if (root.getAttribute('data-cpsb21-dock') === 'on' && !mirror) {
        mirror = new Mirror(root);
        if (!mirror.start()) mirror = null;
      }
      if (root.getAttribute('data-cpsb21-swipe') === 'on'
        || root.getAttribute('data-cpsb21-swipe-colours') === 'on') {
        armRows(root);
      }
    };

    apply();

    if (media.addEventListener) {
      media.addEventListener('change', apply);
    } else if (media.addListener) {
      media.addListener(apply);
    }

    var resize;
    window.addEventListener('resize', function () {
      window.clearTimeout(resize);
      resize = window.setTimeout(function () {
        if (!media.matches) return;
        measure(root);
        if (mirror) mirror.schedule();
      }, 120);
    }, { passive: true });

    /* The header can change height without the window resizing — a longer
       announcement string wrapping to two lines, or a logo swap. */
    if (window.ResizeObserver) {
      var selector = root.getAttribute('data-cpsb21-sticky-selector') || '';
      selector.split(',').forEach(function (part) {
        var sel = part.trim();
        if (!sel) return;
        var node;
        try {
          node = document.querySelector(sel);
        } catch (error) {
          return;
        }
        if (!node) return;
        new ResizeObserver(function () {
          if (media.matches) measure(root);
        }).observe(node);
      });
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.cpsb21'), init);
  }

  ready(boot);
  document.addEventListener('shopify:section:load', boot);
})();

/* ==========================================================================
   PORTRAIT BOWL — Step 2
   Everything above this line is cp-sb21-hero.js, cloned unchanged.

   This half owns the one thing 2.1 does not have: a customer photo. It runs
   beside cp-signature-bowl.js rather than inside it, because that file is
   shared with five other templates and must not move.

   Four jobs:

   1. The upload. Validates size and type in the browser before anything is
      sent, because the alternative is a 15 MB round trip that ends in a
      Shopify error page. The file itself travels as a line item property on
      the same multipart form as the name — no separate upload endpoint, no
      app, no JavaScript needed for the upload itself to work.

   2. The crop. Zoom and drag write one transform, mirrored into the preview
      and serialised into a hidden property so the studio can reproduce it.
      Nothing here is required to place the order: with JavaScript off the
      crop card never appears and the photo arrives uncropped.

   3. The cut-out in the live preview. cp-signature-bowl.js clears the left
      art slot on every render and this repaints it afterwards, on the
      cpsb:state event it fires at the end of that same render. Painting on
      the event rather than polling is what keeps the two in step.

   4. Two copy fixes the shared script cannot make: the {left} token in the
      name counter, and the per-colourway print colour advice, which needs
      the colourway's default ink and so needs the config JSON.
   ========================================================================== */

(function () {
  'use strict';

  var STATE_EVENT = 'cpsb:state';
  var BOOTED = 'cpprtBooted';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function interpolate(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole;
    });
  }

  function clamp(value, low, high) {
    return Math.max(low, Math.min(high, value));
  }

  function init(root) {
    if (root.dataset[BOOTED] === 'on') return;
    root.dataset[BOOTED] = 'on';

    var q = function (name) {
      return root.querySelector('[data-cpprt="' + name + '"]');
    };

    var refs = {
      file: q('photo-file'),
      photo: root.querySelector('.cpprt-photo'),
      thumb: q('photo-thumb'),
      plus: root.querySelector('.cpprt-photo__plus'),
      count: q('photo-count'),
      hint: q('photo-hint'),
      button: q('photo-btn'),
      remove: q('photo-remove'),
      error: q('photo-error'),
      crop: q('crop'),
      cropFrame: q('crop-frame'),
      cropImg: q('crop-img'),
      cropValue: q('crop-value'),
      zoom: q('zoom'),
      zoomLabel: q('zoom-label'),
      reset: q('crop-reset'),
      advice: q('ink-advice'),
      artLeft: root.querySelector('[data-cpsb="preview-art-left"]'),
      nameInput: root.querySelector('[data-cpsb="name-input"]'),
      counter: root.querySelector('[data-cpsb="name-counter"]'),
      cta: root.querySelector('button.cpsb-cta'),
      form: root.querySelector('form[data-type="add-to-cart-form"]')
    };

    if (!refs.file) return;

    // The section id lives on <cpsb-personalizer>, not on the section root —
    // that is the element cp-signature-bowl.js upgrades and the id it stamps
    // into every cpsb:state event. Reading it off the root instead silently
    // filters out every event.
    var host = root.querySelector('cpsb-personalizer');
    var sectionId = host ? host.getAttribute('data-cpsb-section') : null;

    var config = {};
    try {
      var node = root.querySelector('[data-cpsb-config]');
      config = node ? JSON.parse(node.textContent) : {};
    } catch (error) {
      /* a broken config must not take the upload down with it */
    }

    var portrait = config.portrait || {};
    var copy = portrait.copy || {};
    var maxBytes = Number(portrait.maxBytes) || 15 * 1024 * 1024;
    var maxMb = Number(portrait.maxMb) || 15;
    var baseScale = Number(portrait.baseScale) || 1.2;
    var zoomMax = Number(portrait.zoomMax) || 3;

    var state = { src: null, zoom: 1, tx: 0, ty: 0 };
    var drag = null;

    /* ----------------------------------------------------------- geometry */

    function scaleOf(zoom) {
      return zoom * baseScale;
    }

    /* Keep the photo covering the frame: the further it is zoomed, the
       further it may travel before an edge shows. */
    function clampOffset(offset, zoom) {
      var limit = Math.max(0, ((scaleOf(zoom) - 1) / 2) * 100);
      return clamp(offset, -limit, limit);
    }

    function transform() {
      return (
        'translate(' + state.tx.toFixed(2) + '%, ' + state.ty.toFixed(2) + '%) ' +
        'scale(' + scaleOf(state.zoom).toFixed(3) + ')'
      );
    }

    /* --------------------------------------------------------- validation */

    function showError(message) {
      if (refs.error) refs.error.textContent = message || '';
    }

    function accepted(file) {
      if (/^image\//i.test(file.type)) return true;
      // Some Androids hand over HEIC with an empty type, so fall back to the
      // extension rather than rejecting a photo the customer can see.
      return /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || '');
    }

    /* ------------------------------------------------------------ painting */

    function paintCutout() {
      var slot = refs.artLeft;
      if (!slot) return;
      slot.textContent = '';

      if (state.src) {
        var wrap = document.createElement('span');
        wrap.className = 'cpprt-cutout';
        var img = document.createElement('img');
        img.src = state.src;
        img.alt = '';
        img.draggable = false;
        img.style.transform = transform();
        wrap.appendChild(img);
        slot.appendChild(wrap);
        return;
      }

      var empty = document.createElement('span');
      empty.className = 'cpprt-cutout cpprt-cutout--empty';
      var plus = document.createElement('span');
      plus.className = 'cpprt-cutout__plus';
      plus.textContent = '+';
      var label = document.createElement('span');
      label.textContent = 'your photo';
      empty.appendChild(plus);
      empty.appendChild(label);
      slot.appendChild(empty);
    }

    function paintCrop() {
      if (refs.cropImg) refs.cropImg.style.transform = transform();
      if (refs.zoomLabel) {
        refs.zoomLabel.textContent = interpolate(copy.zoomLabel || '{zoom}%', {
          zoom: Math.round(state.zoom * 100)
        });
      }
      if (refs.zoom) refs.zoom.value = String(Math.round(state.zoom * 100));
      if (refs.cropValue) {
        refs.cropValue.value = state.src
          ? 'Zoom ' + Math.round(state.zoom * 100) + '% · ' +
            'X ' + Math.round(state.tx) + '% · ' +
            'Y ' + Math.round(state.ty) + '%'
          : '';
      }
    }

    function paintPhoto() {
      var has = Boolean(state.src);

      if (refs.photo) refs.photo.setAttribute('data-cpprt-has', has ? 'yes' : 'no');
      if (refs.thumb) {
        if (has) {
          refs.thumb.src = state.src;
          refs.thumb.removeAttribute('hidden');
        } else {
          refs.thumb.removeAttribute('src');
          refs.thumb.setAttribute('hidden', '');
        }
      }
      if (refs.cropImg) {
        if (has) refs.cropImg.src = state.src;
        else refs.cropImg.removeAttribute('src');
      }
      if (refs.plus) refs.plus.style.display = has ? 'none' : '';
      if (refs.count) {
        refs.count.textContent = interpolate(copy.count || '{count}/1 uploaded', {
          count: has ? 1 : 0
        });
      }
      if (refs.hint) refs.hint.textContent = has ? copy.hintReady || '' : copy.hintEmpty || '';
      if (refs.button) refs.button.textContent = has ? copy.replace || '' : copy.choose || '';
      if (refs.remove) {
        if (has) refs.remove.removeAttribute('hidden');
        else refs.remove.setAttribute('hidden', '');
      }
      if (refs.crop) {
        if (has) refs.crop.removeAttribute('hidden');
        else refs.crop.setAttribute('hidden', '');
      }

      paintCrop();
      paintCutout();
    }

    function resetCrop() {
      state.zoom = 1;
      state.tx = 0;
      state.ty = 0;
    }

    function clearPhoto() {
      state.src = null;
      resetCrop();
      // Assigning '' is the only cross-browser way to empty a file input, and
      // it must happen or the same file cannot be re-chosen afterwards.
      try {
        refs.file.value = '';
      } catch (error) {
        /* older browsers */
      }
      showError('');
      paintPhoto();
    }

    /* ------------------------------------------------------------- events */

    refs.file.addEventListener('change', function () {
      var file = refs.file.files && refs.file.files[0];
      if (!file) {
        clearPhoto();
        return;
      }

      if (!accepted(file)) {
        clearPhoto();
        showError(copy.errorType || '');
        return;
      }

      if (file.size > maxBytes) {
        var mb = file.size / 1048576;
        clearPhoto();
        showError(
          interpolate(copy.errorSize || '', {
            size: mb >= 10 ? Math.round(mb) : mb.toFixed(1),
            max: maxMb
          })
        );
        return;
      }

      showError('');
      var reader = new FileReader();
      reader.onload = function () {
        state.src = reader.result;
        resetCrop();
        paintPhoto();
      };
      reader.onerror = function () {
        clearPhoto();
        showError(copy.errorType || '');
      };
      reader.readAsDataURL(file);
    });

    if (refs.remove) {
      refs.remove.addEventListener('click', function () {
        clearPhoto();
      });
    }

    if (refs.reset) {
      refs.reset.addEventListener('click', function () {
        resetCrop();
        paintCrop();
        paintCutout();
      });
    }

    if (refs.zoom) {
      refs.zoom.max = String(Math.round(zoomMax * 100));
      refs.zoom.addEventListener('input', function () {
        setZoom(Number(refs.zoom.value) / 100);
      });
    }

    root.querySelectorAll('[data-cpprt="zoom-in"], [data-cpprt="zoom-out"]').forEach(function (button) {
      button.addEventListener('click', function () {
        var step = button.getAttribute('data-cpprt') === 'zoom-in' ? 0.15 : -0.15;
        setZoom(state.zoom + step);
      });
    });

    function setZoom(next) {
      state.zoom = clamp(Math.round(next * 100) / 100, 1, zoomMax);
      state.tx = clampOffset(state.tx, state.zoom);
      state.ty = clampOffset(state.ty, state.zoom);
      paintCrop();
      paintCutout();
    }

    if (refs.cropFrame) {
      refs.cropFrame.addEventListener('pointerdown', function (event) {
        if (!state.src) return;
        var rect = refs.cropFrame.getBoundingClientRect();
        drag = {
          x: event.clientX,
          y: event.clientY,
          w: rect.width,
          h: rect.height,
          tx: state.tx,
          ty: state.ty
        };
        refs.cropFrame.style.cursor = 'grabbing';
        if (refs.cropFrame.setPointerCapture) {
          refs.cropFrame.setPointerCapture(event.pointerId);
        }
      });

      refs.cropFrame.addEventListener('pointermove', function (event) {
        if (!drag) return;
        state.tx = clampOffset(drag.tx + ((event.clientX - drag.x) / drag.w) * 100, state.zoom);
        state.ty = clampOffset(drag.ty + ((event.clientY - drag.y) / drag.h) * 100, state.zoom);
        paintCrop();
        paintCutout();
      });

      ['pointerup', 'pointercancel'].forEach(function (type) {
        refs.cropFrame.addEventListener(type, function (event) {
          drag = null;
          refs.cropFrame.style.cursor = 'grab';
          if (
            event.pointerId != null &&
            refs.cropFrame.hasPointerCapture &&
            refs.cropFrame.hasPointerCapture(event.pointerId)
          ) {
            refs.cropFrame.releasePointerCapture(event.pointerId);
          }
        });
      });
    }

    /* ------------------------------------------- required-photo submit guard
       The `required` attribute already blocks a plain submit, but the theme
       submits over fetch and calls preventDefault, so the same check runs
       here in the capture phase — the same belt-and-braces the shared script
       applies to the name. */
    if (refs.file.required) {
      var guard = function (event) {
        if (state.src) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        showError(copy.errorRequired || '');
        if (refs.photo) refs.photo.scrollIntoView({ block: 'center', behavior: 'smooth' });
        try {
          refs.file.focus({ preventScroll: true });
        } catch (error) {
          /* a clipped input can refuse focus on old Safari */
        }
      };
      if (refs.cta) refs.cta.addEventListener('click', guard, true);
      if (refs.form) refs.form.addEventListener('submit', guard, true);
    }

    /* ---------------------------------------------- copy the shared script
       cannot own: the {left} counter token and the per-colourway advice. */

    function inkAdvice(colourLabel) {
      var colours = config.colours || [];
      var current = null;
      for (var i = 0; i < colours.length; i += 1) {
        if (colours[i].label === colourLabel) {
          current = colours[i];
          break;
        }
      }
      if (!current) return '';
      var template = current.defaultInk === 0 ? copy.adviceLight : copy.adviceDark;
      return interpolate(template || '', { colour: colourLabel });
    }

    function applyState(detail) {
      // cp-signature-bowl.js has just cleared this slot; put the cut-out back.
      paintCutout();

      if (refs.advice) refs.advice.textContent = inkAdvice(detail.colour);

      if (refs.counter && refs.nameInput) {
        var template = (config.copy || {}).counter || '';
        if (template.indexOf('{left}') !== -1) {
          var max = (config.validation || {}).maxLength || refs.nameInput.maxLength;
          var used = refs.nameInput.value.length;
          refs.counter.textContent = interpolate(template, {
            left: Math.max(0, max - used),
            used: used,
            max: max
          });
        }
      }

      var label = root.querySelector('[data-cpsb="preview-config"]');
      var pattern = root.getAttribute('data-cpprt-config');
      if (label && pattern && pattern.indexOf('{ink}') !== -1) {
        var checked = root.querySelector('[data-cpsb="ink-input"]:checked');
        label.textContent = interpolate(pattern, {
          colour: detail.colour || '',
          size: detail.size || '',
          ink: checked ? checked.value : ''
        });
      }
    }

    document.addEventListener(STATE_EVENT, function (event) {
      var detail = event.detail || {};
      if (sectionId && detail.sectionId !== sectionId) return;
      applyState(detail);
    });

    /* cp-signature-bowl.js renders the moment its custom element upgrades,
       which is while that script is still executing — before this deferred
       one runs at all. Its first cpsb:state is therefore always missed, and
       the advice, the config label and the counter would sit unrendered
       until the customer touched something. Replay that opening state from
       the config, which is the same thing the first render was built from. */
    function openingState() {
      var colours = config.colours || [];
      var sizes = config.sizes || [];
      var colour = colours[config.initialColour] || colours[0] || {};
      var size = sizes[config.initialSize] || sizes[0] || {};
      return { sectionId: sectionId, colour: colour.label || '', size: size.label || '' };
    }

    root.querySelectorAll('[data-cpsb="ink-input"]').forEach(function (input) {
      // The shared script re-renders on this too, but its listener is bound
      // first, so the config label would otherwise lag one click behind.
      input.addEventListener('change', function () {
        var label = root.querySelector('[data-cpsb="ink-label"]');
        if (label) label.textContent = input.value;
      });
    });

    paintPhoto();
    applyState(openingState());
  }

  /* ======================================================================
     The composed preview that travels with the order.

     A cart line can carry a name and a print colour as text, but the thing
     that settles a question later is the picture the customer approved. This
     paints the same four layers the preview draws — bowl, cut-out, name,
     rule — onto a canvas and leaves the PNG in a hidden file input, so it
     posts with the form as one more line item property.

     Geometry is read back off the rendered elements rather than recomputed
     from the settings. getBoundingClientRect already accounts for the crop
     transform on the photo, so what gets drawn is what was on screen.

     Everything here is best effort and deliberately silent. Add to cart is
     the one path that must never be blocked, so this runs ahead of time on
     every change and never intercepts the submit: if the canvas is missing,
     an image is cross origin, or a repaint is still in flight, the field
     stays empty and the order goes through with the other properties.
     ====================================================================== */

  function loadImage(src) {
    return new Promise(function (resolve) {
      if (!src) return resolve(null);
      var img = new Image();
      // The bowl comes from Shopify's CDN, which serves permissive CORS — that
      // is what keeps the canvas untainted and toBlob callable. The customer's
      // photo is a data: URI from FileReader, and asking for CORS on one of
      // those makes Chrome fail the load outright, so only remote images get
      // the attribute.
      if (src.slice(0, 5) !== 'data:') img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { resolve(null); };
      img.src = src;
    });
  }

  /* Cover fit, matching object-fit: cover on the source element. */
  function drawCover(ctx, img, x, y, w, h) {
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih || w <= 0 || h <= 0) return;
    var scale = Math.max(w / iw, h / ih);
    var dw = iw * scale;
    var dh = ih * scale;
    ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  }

  function roundedPath(ctx, x, y, w, h, r) {
    var rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    ctx.lineTo(x + rr, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
    ctx.lineTo(x, y + rr);
    ctx.quadraticCurveTo(x, y, x + rr, y);
    ctx.closePath();
  }

  function composePreview(root) {
    try {
      var frame = root.querySelector('[data-cpsb="preview-frame"]');
      if (!frame || !window.HTMLCanvasElement) return Promise.resolve(null);

      var fb = frame.getBoundingClientRect();
      if (fb.width < 40 || fb.height < 40) return Promise.resolve(null);

      var rel = function (node) {
        if (!node) return null;
        var b = node.getBoundingClientRect();
        if (!b.width || !b.height) return null;
        return { x: b.left - fb.left, y: b.top - fb.top, w: b.width, h: b.height };
      };

      var baseEl = frame.querySelector('.cpsb-preview__base:not([hidden])');
      var nameEl = frame.querySelector('.cpsb-preview__name');
      var ruleEl = frame.querySelector('.cpprt-baseline');
      var cutEl = frame.querySelector('.cpsb-preview__art--left');
      // The slot is square; .cpprt-cutout inside it is the shaped element.
      var shapeEl = (cutEl && cutEl.querySelector('.cpprt-cutout')) || cutEl;
      var photoEl = cutEl ? cutEl.querySelector('img') : null;

      var scale = 2;
      var canvas = document.createElement('canvas');
      canvas.width = Math.round(fb.width * scale);
      canvas.height = Math.round(fb.height * scale);
      var ctx = canvas.getContext('2d');
      if (!ctx) return Promise.resolve(null);
      ctx.scale(scale, scale);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, fb.width, fb.height);

      var baseBox = rel(baseEl);
      var shapeBox = rel(shapeEl);
      var photoBox = rel(photoEl);

      return loadImage(baseEl && (baseEl.currentSrc || baseEl.src))
        .then(function (img) {
          if (img && baseBox) drawCover(ctx, img, baseBox.x, baseBox.y, baseBox.w, baseBox.h);
          return photoEl && !photoEl.hidden
            ? loadImage(photoEl.currentSrc || photoEl.src)
            : null;
        })
        .then(function (pimg) {
          /* the cut-out */
          if (pimg && shapeBox && photoBox) {
            ctx.save();
            var cs = window.getComputedStyle(shapeEl);
            // The cut-out is a rounded arch, not a circle, so take the radius
            // the element actually carries rather than assuming half its width.
            var radius = parseFloat(cs.borderTopLeftRadius) || shapeBox.w / 2;
            roundedPath(ctx, shapeBox.x, shapeBox.y, shapeBox.w, shapeBox.h, radius);
            ctx.clip();
            /* photoBox is the post transform box, so the crop the customer
               dragged to is already baked into it. */
            drawCover(ctx, pimg, photoBox.x, photoBox.y, photoBox.w, photoBox.h);
            ctx.restore();
          }

          /* the name */
          var nameBox = rel(nameEl);
          if (nameEl && nameBox) {
            var ns = window.getComputedStyle(nameEl);
            ctx.font = ns.fontStyle + ' ' + ns.fontWeight + ' ' + ns.fontSize + ' ' + ns.fontFamily;
            ctx.fillStyle = ns.color;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            var text = nameEl.textContent || '';
            var metrics = ctx.measureText(text);
            var asc = metrics.actualBoundingBoxAscent;
            var desc = metrics.actualBoundingBoxDescent;
            var baseline = (isFinite(asc) && isFinite(desc))
              ? nameBox.y + (nameBox.h - (asc + desc)) / 2 + asc
              : nameBox.y + nameBox.h;
            ctx.fillText(text, nameBox.x, baseline);
          }

          /* the printed rule */
          var ruleBox = rel(ruleEl);
          if (ruleEl && ruleBox) {
            var rs = window.getComputedStyle(ruleEl);
            ctx.fillStyle = rs.backgroundColor;
            roundedPath(ctx, ruleBox.x, ruleBox.y, ruleBox.w, ruleBox.h, ruleBox.h / 2);
            ctx.fill();
          }

          return new Promise(function (resolve) {
            try {
              canvas.toBlob(function (blob) { resolve(blob || null); }, 'image/png');
            } catch (err) {
              resolve(null);
            }
          });
        })
        .catch(function () { return null; });
    } catch (err) {
      return Promise.resolve(null);
    }
  }

  function bindPreviewCapture(root) {
    var input = root.querySelector('[data-cpprt="preview-file"]');
    if (!input || typeof DataTransfer === 'undefined') return;

    var timer = null;
    var running = false;

    function attach() {
      if (running) return;
      running = true;
      composePreview(root).then(function (blob) {
        running = false;
        if (!blob) return;
        try {
          var file = new File([blob], 'crumpaw-preview.png', { type: 'image/png' });
          var dt = new DataTransfer();
          dt.items.add(file);
          input.files = dt.files;
        } catch (err) {
          /* leaving the field empty is the safe outcome */
        }
      });
    }

    function schedule() {
      window.clearTimeout(timer);
      /* Long enough that dragging the crop does not repaint on every frame,
         short enough to be ready well before anyone reaches Add to cart. */
      timer = window.setTimeout(attach, 450);
    }

    ['change', 'input'].forEach(function (type) {
      root.addEventListener(type, schedule, true);
    });
    root.addEventListener('pointerup', schedule, true);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(schedule);
    }
    window.addEventListener('load', schedule, { once: true });
    schedule();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.cpprt'), function (root) {
      init(root);
      bindPreviewCapture(root);
    });
  }

  ready(boot);
  document.addEventListener('shopify:section:load', boot);
})();
